import React from 'react'
import CustomDropdown from '../../components/shared/dropdown/CustomDropdown';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { Form, Tooltip } from 'react-bootstrap';
import { TooltipSvg } from '../../svgFiles/TooltipSvg';
import './prescription.css';

const PrescriptionList = ({
  listData,
  selectedOption,
  setSelectedOption,
  handleSelect,
  formValues,
  errors = {}, // ✅ Receive errors prop
  currentOrder, disabled
}) => {

  const toNumberOrNull = (val) => {
    if (val === null || val === undefined || val === "") return null;
    const n = Number(val);
    return Number.isNaN(n) ? null : n;
  };

  // AI extraction can return valid values that are not part of the standard
  // quarter-diopter list (for example -0.87 CYL). Keep the exact source value
  // visible instead of silently rounding it to a nearby option.
  const dropdownOptions = (options = [], currentValue) => {
    const mappedOptions = options.map(opt => ({ label: opt, value: Number(opt) }));
    const numericValue = toNumberOrNull(currentValue);

    if (numericValue !== null && !mappedOptions.some(opt => opt.value === numericValue)) {
      return [{ label: String(currentValue), value: numericValue }, ...mappedOptions];
    }

    return mappedOptions;
  };

  const isSingleVision = ["single vision", "reading lenses"].includes(
    currentOrder?.prescriptionType?.toLowerCase()
  );

  const leftColumnCount = ["SPH", "CYL", "AXIS"].filter(
    (field) => listData?.data?.fields?.OD_Right?.[field]?.length > 0
  ).length || 1;
  const hasAddColumn = !isSingleVision && listData?.data?.fields?.OD_Right?.ADD?.length > 0;
  const hasPdColumn = listData?.data?.fields?.OD_Right?.PD?.length > 0;
  const rightColumnCount = Number(hasAddColumn) + Number(hasPdColumn) + 1;

  const pdOptionIdSuffix = String(currentOrder?.ItemID || "prescription")
    .replace(/[^a-zA-Z0-9_-]/g, "-");

  const selectPDOption = (option) => {
    setSelectedOption(option);
    handleSelect("PDOneorTwoNumbers", option);
  };

  return (
    <div>
      <div className='lens-drops'>
        <div
          className='drop-partitions'
          style={{ "--partition-columns": leftColumnCount }}
        >
          {/* HEADERS */}
          <ul className="p-0">
            <li className="opacity-0">SPH</li>
            {listData?.data?.fields?.OD_Right?.SPH?.length > 0 && (<li>SPH</li>)}
            {listData?.data?.fields?.OD_Right?.CYL?.length > 0 && (<li>CYL</li>)}
            {listData?.data?.fields?.OD_Right?.AXIS?.length > 0 && (<li>AXIS</li>)}
          </ul>

          {/* OD (Right) */}
          <ul className="p-0">
            <li><span className="or-index lens-index">OD (R)</span></li>

            {listData?.data?.fields?.OD_Right?.SPH?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OD_Right.SPH, formValues?.ODRightSphere_SPH)}
                  value={toNumberOrNull(formValues?.ODRightSphere_SPH)}
                  onSelect={(opt) => handleSelect("ODRightSphere_SPH", Number(opt.value))}
                  error={errors['ODRightSphere_SPH']} // ✅ Pass error state
                />
              </li>
            )}

            {listData?.data?.fields?.OD_Right?.CYL?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OD_Right.CYL, formValues?.ODRightCylinder_CYL)}
                  value={toNumberOrNull(formValues?.ODRightCylinder_CYL)}
                  onSelect={(opt) => handleSelect("ODRightCylinder_CYL", Number(opt.value))}
                  error={errors['ODRightCylinder_CYL']} // ✅ Pass error state
                />
              </li>
            )}

            {listData?.data?.fields?.OD_Right?.AXIS?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OD_Right.AXIS, formValues?.ODRightAxis)}
                  value={toNumberOrNull(formValues?.ODRightAxis)}
                  onSelect={(opt) => handleSelect("ODRightAxis", Number(opt.value))}
                  error={errors['ODRightAxis']} // ✅ Pass error state
                />
              </li>
            )}

          </ul>

          {/* OS (Left) */}
          <ul className="p-0">
            <li><span className="os-index lens-index">OS (L)</span></li>

            {listData?.data?.fields?.OS_Left?.SPH?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OS_Left.SPH, formValues?.OSLeftSphere_SPH)}
                  value={toNumberOrNull(formValues?.OSLeftSphere_SPH)}
                  onSelect={(opt) => handleSelect("OSLeftSphere_SPH", Number(opt.value))}
                  error={errors['OSLeftSphere_SPH']} // ✅ Pass error state
                />
              </li>
            )}

            {listData?.data?.fields?.OS_Left?.CYL?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OS_Left.CYL, formValues?.OSLeftCylinder_CYL)}
                  value={toNumberOrNull(formValues?.OSLeftCylinder_CYL)}
                  onSelect={(opt) => handleSelect("OSLeftCylinder_CYL", Number(opt.value))}
                  error={errors['OSLeftCylinder_CYL']} // ✅ Pass error state
                />
              </li>
            )}

            {listData?.data?.fields?.OS_Left?.AXIS?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OS_Left.AXIS, formValues?.OSLeftAxis)}
                  value={toNumberOrNull(formValues?.OSLeftAxis)}
                  onSelect={(opt) => handleSelect("OSLeftAxis", Number(opt.value))}
                  error={errors['OSLeftAxis']} // ✅ Pass error state
                />
              </li>
            )}

          </ul>
        </div>

        {/* SECOND PARTITION */}
        <div
          className='drop-partitions right-partition'
          style={{ "--partition-columns": rightColumnCount }}
        >
          <ul className="p-0">
            <li className="opacity-0 mobile-show">SPH</li>
            {!isSingleVision && listData?.data?.fields?.OD_Right?.ADD?.length > 0 && (<li>ADD</li>)}
            <li className="pd-header-cell">
              <span className="pd-header-label">
                PD
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id={`pd-tooltip-${pdOptionIdSuffix}`}>
                      <div style={{ padding: "4px 8px", background: "#000", color: "#fff", borderRadius: "4px" }}>
                        Pupillary Distance
                      </div>
                    </Tooltip>
                  }
                >
                  <span className="pd-tooltip-trigger">
                    <TooltipSvg />
                  </span>
                </OverlayTrigger>
              </span>
              <span className="pd-option-toggle">
                <Form.Check
                  disabled={disabled}
                  type="radio"
                  id={`oneNumber-${pdOptionIdSuffix}`}
                  name={`pdOption-${pdOptionIdSuffix}`}
                  label="One"
                  checked={selectedOption === "One"}
                  onChange={() => selectPDOption("One")}
                />
                <Form.Check
                  disabled={disabled}
                  type="radio"
                  id={`twoNumber-${pdOptionIdSuffix}`}
                  name={`pdOption-${pdOptionIdSuffix}`}
                  label="Two"
                  checked={selectedOption === "Two"}
                  onChange={() => selectPDOption("Two")}
                />
              </span>
            </li>
            <li>PRISM</li>
          </ul>

          {/* OD (Right) */}
          <ul className="p-0">
            <li className='mobile-show'><span className="or-index lens-index">OD (Right)</span></li>

            {!isSingleVision && listData?.data?.fields?.OD_Right?.ADD?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OD_Right.ADD, formValues?.ODRightADD)}
                  value={toNumberOrNull(formValues?.ODRightADD)}
                  onSelect={(opt) => handleSelect("ODRightADD", Number(opt.value))}
                  error={errors['ODRightADD']} // ✅ Pass error state
                />
              </li>
            )}

            {listData?.data?.fields?.OD_Right?.PD?.length > 0 && (
              <li>
                {formValues?.SingularPDValue === 'One' || selectedOption === "One" ?
                  <CustomDropdown portalMenu
                    disabled={disabled}
                    label={"0.00"}
                    options={dropdownOptions(listData.data.fields.OD_Right.SPD, formValues?.SingularPDValue)}
                    value={toNumberOrNull(formValues?.SingularPDValue)}
                    onSelect={(opt) => handleSelect("SingularPDValue", opt)}
                    error={errors['SingularPDValue']}
                  /> : <CustomDropdown portalMenu
                    disabled={disabled}
                    label={"0.00"}
                    options={dropdownOptions(listData.data.fields.OD_Right.PD, formValues?.ODRightPupillaryDistance_PD)}
                    value={toNumberOrNull(formValues?.ODRightPupillaryDistance_PD)}
                    onSelect={(opt) => handleSelect("ODRightPupillaryDistance_PD", opt)}
                    error={errors['ODRightPupillaryDistance_PD']}
                  />}

              </li>
            )}
            <li>
              <input
                disabled={disabled}
                type="text"
                className={`cp-input prism-input ${errors.ODRightPrism ? "error" : ""}`}
                value={formValues?.ODRightPrism ?? ""}
                placeholder="Enter"
                maxLength={32}
                onChange={(event) => handleSelect("ODRightPrism", event.target.value)}
              />
            </li>
          </ul>

          {/* OS (Left) */}
          <ul className="p-0">
            <li className='mobile-show'><span className="os-index lens-index">OS (Left)</span></li>

            {!isSingleVision && listData?.data?.fields?.OS_Left?.ADD?.length > 0 && (
              <li>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OS_Left.ADD, formValues?.OSLeftADD)}
                  value={toNumberOrNull(formValues?.OSLeftADD)}
                  onSelect={(opt) => handleSelect("OSLeftADD", Number(opt.value))}
                  error={errors['OSLeftADD']} // ✅ Pass error state
                />
              </li>
            )}

            {/* Hide PD when oneNumber selected */}
            {listData?.data?.fields?.OS_Left?.PD?.length > 0 && (
              <li className={`${selectedOption === "One" ? "opacity-0" : ""}`}>
                <CustomDropdown portalMenu
                  disabled={disabled}
                  label={"0.00"}
                  options={dropdownOptions(listData.data.fields.OS_Left.PD, formValues?.OSLeftPupillaryDistance_PD)}
                  value={toNumberOrNull(formValues?.OSLeftPupillaryDistance_PD)}
                  onSelect={(opt) => handleSelect("OSLeftPupillaryDistance_PD", Number(opt.value))}
                  error={errors['OSLeftPupillaryDistance_PD']} // ✅ Pass error state
                />
              </li>
            )}
            <li>
              <input
                disabled={disabled}
                type="text"
                className={`cp-input prism-input ${errors.OSLeftPrism ? "error" : ""}`}
                value={formValues?.OSLeftPrism ?? ""}
                placeholder="Enter"
                maxLength={32}
                onChange={(event) => handleSelect("OSLeftPrism", event.target.value)}
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default PrescriptionList
